import { useMemo, useRef, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { textFor } from '../localizedText';
import {
  signalCheckMarker,
  signalCountPill,
  signalMono,
  signalOptionDescription,
  signalOptionLabel,
  signalOptionRow,
  signalRevealed,
  signalTextField
} from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  isExclusive,
  MultiSelectOption,
  readMultiSelectAnswer,
  readMultiSelectConfig,
  toggleSelection
} from './multiSelect';
import { BlockRendererProps, questionIdOf } from './types';

/** Show the character counter only once it starts to matter, not from the first keystroke. */
const COUNTER_VISIBLE_FROM = 0.8;

/**
 * `multi_select@1`: any number of choices from a list.
 *
 * Built as a real checkbox group rather than a row of toggles, because that is what it is: assistive
 * technology announces "2 of 5, checked", and the square marker says "any of these" before a word of
 * copy is read. Tapping is the primary route and always works.
 *
 * The behaviour worth reading closely is the **two kinds of number** this block shows. A hard limit
 * is enforced — reaching the maximum stops further selections, exactly as the server would. A
 * recommendation is not: the counter acknowledges it, and going outside it neither blocks Next nor
 * disables anything. Conflating them is the failure this whole type is shaped to avoid (catalog
 * §3.2), so the two never share a control.
 *
 * "None of these" is exclusive and is handled where the selection changes rather than by disabling
 * rows, because a disabled option cannot explain itself. Tapping it clears the rest; tapping
 * anything else clears it.
 */
export const MultiSelectBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const config = useMemo(() => readMultiSelectConfig(block), [block]);
  const { options, maxSelections, recommendation, showDescriptions } = config;

  const parsed = readMultiSelectAnswer(answer);
  const selected = parsed?.optionKeys ?? [];
  const otherText = parsed?.otherText ?? '';

  const [focusIndex, setFocusIndex] = useState(0);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const otherFieldRef = useRef<HTMLInputElement | null>(null);
  const questionId = questionIdOf(block);

  const isOtherSelected = options.some(
    option => option.kind === 'OTHER' && selected.includes(option.key)
  );

  const apply = (next: string[]) => {
    const stillHasOther = options.some(
      option => option.kind === 'OTHER' && next.includes(option.key)
    );
    onChange({
      // Dropping "other" must drop its text: keeping it would submit free text the server refuses
      // for a selection that no longer includes an "other" option.
      otherText: stillHasOther ? otherText || null : null,
      optionKeys: next
    });
  };

  const toggle = (option: MultiSelectOption) => {
    const next = toggleSelection(selected, option, options);
    // The hard maximum is enforced here rather than by disabling rows: a row that stops responding
    // is indistinguishable from a broken one, while a tap that simply does not add is at least
    // consistent with the counter sitting right below it.
    const ordinaryCount = next.filter(key => {
      const entry = options.find(candidate => candidate.key === key);
      return entry !== undefined && !isExclusive(entry);
    }).length;
    if (
      maxSelections !== null &&
      ordinaryCount > maxSelections &&
      !selected.includes(option.key)
    ) {
      return;
    }
    apply(next);
    if (option.kind === 'OTHER' && !selected.includes(option.key)) {
      // Move focus into the field the choice just revealed, so the extra step is where the cursor
      // already is.
      window.requestAnimationFrame(() => otherFieldRef.current?.focus());
    }
  };

  // Arrow keys move focus WITHOUT selecting, which is the native checkbox-group behaviour and the
  // opposite of a radio group: here moving and choosing are separate acts, and Space chooses.
  const moveFocus = (delta: number) => {
    if (!options.length) {
      return;
    }
    const next = (focusIndex + delta + options.length) % options.length;
    setFocusIndex(next);
    optionRefs.current[next]?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      moveFocus(1);
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      moveFocus(-1);
    }
  };

  const chosenOptions = options.filter(option => selected.includes(option.key));
  const hasExclusive = chosenOptions.some(isExclusive);
  const ordinaryCount = chosenOptions.filter(
    option => !isExclusive(option)
  ).length;
  // "None of these" is a reply, not a count, so it is judged against no range at all — the same
  // rule the server derives by.
  const withinRecommendation =
    recommendation !== null &&
    !hasExclusive &&
    ordinaryCount >= recommendation.min &&
    ordinaryCount <= recommendation.max;

  const guidance = guidanceTextOf(config, locale);
  const remaining = config.otherTextMaxLength - otherText.length;
  const showCounter =
    otherText.length >= config.otherTextMaxLength * COUNTER_VISIBLE_FROM;

  return (
    <Stack spacing={3}>
      {/*
        The hint says that more than one is allowed at all, and it says it ALONGSIDE the operator's
        help rather than instead of it — the two have different authors, and an operator who wrote a
        line of help should not thereby silence the one fact the control does not show. The LIMITS
        are not repeated here: they live under the counter, next to the number they bound.
      */}
      <BlockHeading
        block={block}
        locale={locale}
        hint={getTrans('guestForm.multiSelect.hint')}
      />

      <Box
        role="group"
        aria-labelledby={questionId}
        onKeyDown={handleKeyDown}
        sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}
      >
        {options.map((option, index) => {
          const isSelected = selected.includes(option.key);
          const description = showDescriptions
            ? textFor(option.description, locale)
            : '';
          return (
            <ButtonBase
              key={option.key}
              ref={element => {
                optionRefs.current[index] = element;
              }}
              role="checkbox"
              aria-checked={isSelected}
              tabIndex={index === focusIndex ? 0 : -1}
              onClick={() => {
                setFocusIndex(index);
                toggle(option);
              }}
              data-testid={`form-option-${option.key}`}
              // Dashed for both escapes: "none of these" leaves the list behind and "something
              // else" asks a further question. Neither settles the question the way a choice does.
              sx={signalOptionRow(isSelected, option.kind !== 'CHOICE')}
            >
              <Box aria-hidden="true" sx={signalCheckMarker(isSelected)}>
                <CheckIcon />
              </Box>
              <Box sx={signalOptionLabel}>
                {textFor(option.label, locale)}
                {description && (
                  <Box sx={signalOptionDescription}>{description}</Box>
                )}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>

      {/* The running total, announced as it changes: a counter nobody hears is no feedback at all
          for a screen reader, and it is the only acknowledgement a selection gets. */}
      <Stack spacing={1} role="status" aria-live="polite">
        <Box
          component="span"
          sx={signalCountPill(withinRecommendation)}
          data-testid="form-selection-count"
        >
          {getTransWithParams('guestForm.multiSelect.selected', {
            count: selected.length
          })}
        </Box>
        {guidance && (
          <Typography variant="caption" color="text.secondary">
            {guidance}
          </Typography>
        )}
      </Stack>

      {isOtherSelected && (
        <Box sx={signalRevealed}>
          <TextField
            inputRef={otherFieldRef}
            value={otherText}
            onChange={event =>
              onChange({ optionKeys: selected, otherText: event.target.value })
            }
            label={getTrans('guestForm.other.label')}
            inputProps={{
              maxLength: config.otherTextMaxLength,
              'data-testid': 'form-other-text'
            }}
            helperText={
              showCounter
                ? getTransWithParams('guestForm.other.remaining', { remaining })
                : ' '
            }
            FormHelperTextProps={{ sx: signalMono }}
            sx={signalTextField}
            fullWidth
            multiline
            minRows={2}
          />
        </Box>
      )}
    </Stack>
  );
};

/**
 * One line under the counter, and a deliberate precedence.
 *
 * A recommendation is shown in preference to the hard limits, because it is the more useful thing
 * to say: the limits are already enforced by the control itself, while the advice only exists if
 * someone reads it. Its own configured message wins over the generic phrasing, since an operator who
 * wrote one had a reason.
 */
const guidanceTextOf = (
  config: ReturnType<typeof readMultiSelectConfig>,
  locale: string
): string => {
  const { recommendation, minSelections, maxSelections } = config;
  if (recommendation) {
    return (
      textFor(recommendation.message, locale) ||
      getTransWithParams('guestForm.multiSelect.recommended', {
        min: recommendation.min,
        max: recommendation.max
      })
    );
  }
  if (minSelections !== null && maxSelections !== null) {
    return getTransWithParams('guestForm.multiSelect.between', {
      min: minSelections,
      max: maxSelections
    });
  }
  if (minSelections !== null) {
    return getTransWithParams('guestForm.multiSelect.atLeast', {
      min: minSelections
    });
  }
  if (maxSelections !== null) {
    return getTransWithParams('guestForm.multiSelect.atMost', {
      max: maxSelections
    });
  }
  return '';
};
